import React, { memo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Organisation } from '@common/types/objects';
import { CertificateBadge } from './CertificateBadge';

export interface CertificateProps {
  courseName: string;
  employeeName: string;
  completionDate: string;
  currentPoints: number;
  maxPoints: number;
  org: Organisation;
  /** Pre-loaded org logo as base64 to avoid CORS issues */
  orgLogoBase64?: string | null;
}

const ONETEAM_LOGO = '/static/images/logo_large.png';
const DEFAULT_PRIMARY_COLOR = '#2563EB';

// Generate SVG with primary color as background and lighter decorative shapes
function generateBackgroundSVG(primaryColor: string): string {
  // Generate unique IDs to avoid conflicts when multiple certificates are rendered
  const uniqueId = Math.random().toString(36).substring(2, 9);
  const clipId = `clip0_${uniqueId}`;
  const gradient0Id = `paint0_linear_${uniqueId}`;
  const gradient1Id = `paint1_linear_${uniqueId}`;
  const gradient2Id = `paint2_linear_${uniqueId}`;

  // SVG with solid background + white/light decorative shapes that fade to transparent
  // This creates the effect of lighter shapes on a colored background
  const path1 = 'M-19.5 642.045C102.831 642.045 202 741.214 202 863.545C202 985.876 102.831 1085.05 '
    + '-19.5 1085.05C-141.831 1085.05 -241 985.876 -241 863.545C-241 741.214 -141.831 642.045 -19.5 642.045Z';
  const path2 = 'M-211.324 -174.205C-150.159 -280.147 -14.6917 -316.445 91.25 -255.279C197.192 -194.114 '
    + '233.49 -58.6468 172.325 47.2949C111.16 153.237 -24.3082 189.536 -130.25 128.37C-236.192 67.2045 '
    + '-272.49 -68.2633 -211.324 -174.205Z';

  return `<svg width="192" height="649" viewBox="0 0 192 649" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="192" height="649" fill="${primaryColor}"/>
<g clip-path="url(#${clipId})" opacity="0.3">
<path d="${path1}" stroke="url(#${gradient0Id})" stroke-width="100"/>
<path d="${path2}" stroke="url(#${gradient1Id})" stroke-width="100"/>
<circle cx="217.5" cy="400.5" r="153.5" stroke="url(#${gradient2Id})" stroke-width="100"/>
</g>
<defs>
<linearGradient id="${gradient0Id}" x1="-291" y1="863.545" x2="252" y2="863.545" gradientUnits="userSpaceOnUse">
<stop stop-color="white"/>
<stop offset="1" stop-color="white" stop-opacity="0"/>
</linearGradient>
<linearGradient id="${gradient1Id}" x1="116.25" y1="-298.581" x2="-155.25" y2="171.671" gradientUnits="userSpaceOnUse">
<stop stop-color="white"/>
<stop offset="1" stop-color="white" stop-opacity="0"/>
</linearGradient>
<linearGradient id="${gradient2Id}" x1="217.5" y1="197" x2="217.5" y2="604" gradientUnits="userSpaceOnUse">
<stop stop-color="white"/>
<stop offset="1" stop-color="white" stop-opacity="0"/>
</linearGradient>
<clipPath id="${clipId}">
<rect width="192" height="800" fill="white"/>
</clipPath>
</defs>
</svg>`;
}

export const Certificate = memo(({
  courseName,
  employeeName,
  completionDate,
  currentPoints,
  maxPoints,
  org,
  orgLogoBase64,
}: CertificateProps) => {
  const percentage = maxPoints > 0 ? Math.round((currentPoints / maxPoints) * 100) : 0;
  const formattedDate = completionDate ? moment(completionDate).format('D MMMM, YYYY') : '';
  const { t } = useTranslation();

  // Get primary color from org theme, fallback to default blue
  const primaryColor = org.theme?.main_color || DEFAULT_PRIMARY_COLOR;

  // Check if org is whitelabel
  const isWhitelabel = !!org.whitelabel_config;

  // Get org logo - prefer base64 if available (CORS-safe), fallback to original URL
  const orgLogo = orgLogoBase64 || org.brand_icon;

  return (
    <div
      className="tw-bg-white tw-w-full tw-max-h-[650px] tw-max-w-[1200px]"
      style={{ width: '1200px', height: '650px' }}
    >
      <div className="tw-flex tw-h-full">
        {/* Left panel with primary color SVG background */}
        <div className="tw-w-[192px] tw-relative tw-overflow-hidden tw-flex-shrink-0">
          <img
            src={`data:image/svg+xml,${encodeURIComponent(generateBackgroundSVG(primaryColor))}`}
            alt=""
            className="tw-w-full tw-h-full tw-object-cover tw-object-left"
          />
        </div>
        <div className="tw-flex-1 tw-bg-white tw-p-12 tw-flex tw-flex-row tw-relative">
          <div className="tw-flex tw-justify-between tw-flex-col tw-items-start tw-flex-1">
            <div>
              {/* Show Oneteam logo only for non-whitelabel orgs */}
              {!isWhitelabel && ONETEAM_LOGO && (
                <img
                  src={ONETEAM_LOGO}
                  alt="OneTeam"
                  className="tw-h-10"
                />
              )}
              {/* Show org logo for whitelabel orgs */}
              {isWhitelabel && orgLogo && (
                <img
                  src={orgLogo}
                  alt={org.name}
                  className="tw-h-10 tw-object-contain tw-hidden"
                />
              )}
            </div>
            <div>
              <div className="tw-text-gray-muted tw-text-2xl tw-font-semibold tw-uppercase tw-tracking-wider tw-mb-3">
                {t('learning:certificate_of_completion')}
              </div>
              <h1 className="tw-text-[72px] tw-font-medium tw-text-black tw-leading-tight">
                {courseName}
              </h1>
            </div>
            <div>
              <div className="tw-text-gray-muted tw-text-2xl tw-font-semibold tw-uppercase tw-tracking-wider tw-mb-3">
                {t('learning:proudly_presented_to')}
              </div>
              <h2 className="tw-text-[54px] tw-font-medium tw-text-black tw-mb-3 tw-leading-tight">
                {employeeName}
              </h2>
              <div className="tw-text-3xl tw-font-medium tw-text-gray-muted tw-leading-relaxed">
                {t('learning:for_successfully_completing_the_course_on')}
                <strong className="tw-text-black"> {formattedDate}</strong>,{' '}
                {t('learning:achieving_a_score_of')}
                <strong className="tw-text-black"> {currentPoints}/{maxPoints} ({percentage}%).</strong>
              </div>
            </div>
          </div>

          {/* Certificate Badge - SVG with dynamic colors */}
          <div className="tw-w-[180px] tw-flex tw-items-start tw-justify-end tw-flex-shrink-0">
            <CertificateBadge
              primaryColor={primaryColor}
              isWhitelabel={isWhitelabel}
              orgLogo={orgLogo}
              size={150}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

Certificate.displayName = 'Certificate';
